import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  TrendingUp, Minus, Loader2, RefreshCcw, Database, CheckCircle2,
  XCircle, Info, BarChart3, ArrowUp, ArrowDown, Search,
} from "lucide-react";
import { toast } from "sonner";

/**
 * CseTrackRecordTab — the original Phase 15 CSE Track Record dashboard
 * extracted into a self-contained component so /track-record can host
 * a CSE / US / Combined tab layout (Phase 5).
 *
 * Voting-class .N0000 BUY / STRONG BUY signals benchmarked vs ASPI.
 * Visible to all authenticated users; admins additionally see
 * Refresh + Rebuild buttons.
 */

const fmtPct = (v, opts = {}) => {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(opts.decimals ?? 2)}%`;
};

const fmtPrice = (v) => {
  if (v == null) return "—";
  return `Rs. ${Number(v).toFixed(2)}`;
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};

const normaliseSignal = (s) => (s || "").toString().toUpperCase().replace(/[\s_]+/g, "_");

const signalBadgeClass = (s) => {
  const norm = normaliseSignal(s);
  if (norm === "STRONG_BUY") return "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40";
  if (norm === "BUY") return "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/40";
  if (norm === "WATCH") return "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40";
  if (norm === "HOLD" || norm === "NEUTRAL") return "bg-muted text-muted-foreground border-muted-foreground/30";
  if (norm === "AVOID" || norm === "SELL" || norm === "STRONG_SELL") return "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40";
  return "bg-muted text-muted-foreground";
};

const VALUATION_LABEL = {
  significantly_undervalued: "Significantly Undervalued",
  undervalued: "Undervalued",
  fair_value: "Fair Value",
  fairly_valued: "Fair Value",
  overvalued: "Overvalued",
  significantly_overvalued: "Significantly Overvalued",
};
const VALUATION_BADGE_CLASS = {
  significantly_undervalued: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40",
  undervalued: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  fair_value: "bg-muted text-muted-foreground border-muted-foreground/30",
  fairly_valued: "bg-muted text-muted-foreground border-muted-foreground/30",
  overvalued: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  significantly_overvalued: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40",
};

const returnClass = (v) => {
  if (v == null) return "text-muted-foreground";
  if (v > 0) return "text-[hsl(var(--success))]";
  if (v < 0) return "text-[hsl(var(--destructive))]";
  return "text-muted-foreground";
};

export const CseTrackRecordTab = ({ onSummaryLoaded }) => {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [signalFilter, setSignalFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [sortBy, setSortBy] = useState({ key: "return_pct", dir: "desc" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, limit: "500" });
      if (signalFilter !== "all") params.append("signal", signalFilter);
      const res = await apiGet(`/track-record?${params.toString()}`);
      setRows(res.rows || []);
      setSummary(res.summary || null);
      onSummaryLoaded?.(res.summary || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load track record");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, signalFilter, onSummaryLoaded]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows;
    if (q) {
      out = out.filter((r) => (
        r.cse_symbol.toLowerCase().includes(q) ||
        (r.company_name || "").toLowerCase().includes(q) ||
        (r.sector || "").toLowerCase().includes(q)
      ));
    }
    const { key, dir } = sortBy;
    out = [...out].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return dir === "asc" ? av - bv : bv - av;
      }
      return dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return out;
  }, [rows, search, sortBy]);

  const onSort = (key) => {
    setSortBy((prev) => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "desc" });
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await apiPost("/admin/track-record/refresh");
      toast.success(`Snapshot complete — ${r.new_episodes} new, ${r.updated_episodes} updated, ${r.closed_episodes} closed`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const onBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await apiPost("/admin/track-record/backfill?wipe=true");
      toast.success(`Backfill complete — ${r.backfill.episodes_created} episodes from ${r.backfill.symbols_scanned} symbols`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const s = summary || {};
  return (
    <div className="space-y-6" data-testid="cse-track-record-tab">
      {/* Header summary + admin controls */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Every BUY and STRONG BUY signal we publish — tracked from the day it
            was issued vs the ASPI benchmark. Voting-class shares only. Updated
            daily at 18:30 SLT after the CSE close.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing || backfilling}
              className="gap-1.5"
              data-testid="track-record-refresh"
            >
              {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onBackfill}
              disabled={refreshing || backfilling}
              className="gap-1.5"
              data-testid="track-record-backfill"
            >
              {backfilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
              Rebuild from history
            </Button>
          </div>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryTile
          label="Active BUY / STRONG BUY"
          value={`${s.active_signals ?? "—"}`}
          sub={`${s.strong_buy_active ?? 0} STRONG · ${s.buy_active ?? 0} BUY`}
          icon={TrendingUp}
          testid="track-record-kpi-active"
        />
        <SummaryTile
          label="Avg. return (active)"
          value={fmtPct(s.avg_return_active_pct)}
          sub={`vs ASPI ${fmtPct(s.avg_aspi_return_pct)}`}
          icon={BarChart3}
          tone={s.avg_return_active_pct}
          testid="track-record-kpi-return"
        />
        <SummaryTile
          label="Accuracy (price up)"
          value={s.accuracy_price_up_pct != null ? `${s.accuracy_price_up_pct.toFixed(1)}%` : "—"}
          sub={`Active: ${s.accuracy_price_up_active_pct != null ? `${s.accuracy_price_up_active_pct.toFixed(1)}%` : "—"}`}
          icon={CheckCircle2}
          testid="track-record-kpi-price-up"
        />
        <SummaryTile
          label="Accuracy (vs ASPI)"
          value={s.accuracy_vs_aspi_pct != null ? `${s.accuracy_vs_aspi_pct.toFixed(1)}%` : "—"}
          sub={`Active: ${s.accuracy_vs_aspi_active_pct != null ? `${s.accuracy_vs_aspi_active_pct.toFixed(1)}%` : "—"}`}
          icon={CheckCircle2}
          testid="track-record-kpi-vs-aspi"
        />
      </div>

      {/* ASPI reference */}
      {s.aspi_current && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-2">
          <Info className="h-3 w-3" />
          ASPI benchmark: <span className="font-mono text-foreground">{Number(s.aspi_current).toFixed(2)}</span>
          {s.aspi_current_date && <> as of {fmtDate(s.aspi_current_date)}</>}
          {" · "} {s.total_signals} tracked signals ({s.active_signals} active, {s.ended_signals} ended)
        </div>
      )}

      {/* Filters */}
      <Card className="border bg-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList data-testid="track-record-status-tabs">
                <TabsTrigger value="active" data-testid="track-record-tab-active">Active</TabsTrigger>
                <TabsTrigger value="ended" data-testid="track-record-tab-ended">Ended</TabsTrigger>
                <TabsTrigger value="all" data-testid="track-record-tab-all">All</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select value={signalFilter} onValueChange={setSignalFilter}>
              <SelectTrigger className="w-[180px]" data-testid="track-record-signal-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All signals</SelectItem>
                <SelectItem value="STRONG BUY">STRONG BUY only</SelectItem>
                <SelectItem value="BUY">BUY only</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[220px] max-w-[400px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search symbol, company, sector…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="track-record-search"
              />
            </div>
            <div className="text-xs text-muted-foreground ml-auto">
              {filtered.length} rows
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border bg-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading track record…
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-[hsl(var(--destructive))]">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">
              No records yet.
              {isAdmin && (
                <div className="mt-2 text-xs">
                  Use <span className="font-mono">Rebuild from history</span> above to seed from score_history, or wait for the next 18:30 SLT snapshot.
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="track-record-table">
                <TableHeader>
                  <TableRow>
                    <HeaderCell label="Stock" sortKey="cse_symbol" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Signal" sortKey="entry_signal" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Signal Date" sortKey="signal_date" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Signal Price" sortKey="signal_price" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Current" sortKey="current_price" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Score" sortKey="live_score" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Deep AI" sortKey="deep_ai_signal" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="TV Signal" sortKey="tv_signal" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Valuation" sortKey="valuation_status" sortBy={sortBy} onSort={onSort} />
                    <HeaderCell label="Target" sortKey="target_price" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Upside" sortKey="target_upside_pct" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Return" sortKey="return_pct" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="vs ASPI" sortKey="excess_return_pct" sortBy={sortBy} onSort={onSort} align="right" />
                    <HeaderCell label="Price ↑" sortKey="correct_price_up" sortBy={sortBy} onSort={onSort} align="center" />
                    <HeaderCell label="Beat ASPI" sortKey="correct_vs_aspi" sortBy={sortBy} onSort={onSort} align="center" />
                    <HeaderCell label="Days" sortKey="days_held" sortBy={sortBy} onSort={onSort} align="right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.record_id} data-testid={`track-record-row-${r.cse_symbol}`}>
                      <TableCell className="align-middle">
                        <div className="flex flex-col">
                          <span className="font-mono text-xs font-semibold">{r.cse_symbol}</span>
                          <span className="text-[10px] text-muted-foreground truncate max-w-[220px]" title={r.company_name}>
                            {r.company_name || r.company_code}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline" className={`text-[10px] w-fit ${signalBadgeClass(r.entry_signal)}`}>
                            {r.entry_signal}
                          </Badge>
                          {!r.signal_active && (
                            <Badge variant="outline" className="text-[9px] w-fit bg-muted">
                              ENDED · {r.ended_signal || "—"}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs tabular-nums">{fmtDate(r.signal_date)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmtPrice(r.signal_price)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmtPrice(r.current_price)}</TableCell>
                      <TableCell
                        className="text-right font-mono text-xs font-semibold"
                        data-testid={`track-record-score-${r.cse_symbol}`}
                      >
                        {r.live_score != null ? Math.round(r.live_score) : "—"}
                      </TableCell>
                      <TableCell>
                        {r.deep_ai_signal ? (
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${signalBadgeClass(r.deep_ai_signal)}`}
                            data-testid={`track-record-deep-${r.cse_symbol}`}
                          >
                            {normaliseSignal(r.deep_ai_signal).replace(/_/g, " ")}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.tv_signal ? (
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${signalBadgeClass(r.tv_signal)}`}
                            data-testid={`track-record-tv-${r.cse_symbol}`}
                          >
                            {normaliseSignal(r.tv_signal).replace(/_/g, " ")}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.valuation_status ? (
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${VALUATION_BADGE_CLASS[r.valuation_status] || "bg-muted text-muted-foreground border-muted-foreground/30"}`}
                            data-testid={`track-record-valuation-${r.cse_symbol}`}
                            title={VALUATION_LABEL[r.valuation_status] || r.valuation_status}
                          >
                            {VALUATION_LABEL[r.valuation_status] || r.valuation_status.replace(/_/g, " ")}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell
                        className="text-right font-mono text-xs"
                        data-testid={`track-record-target-${r.cse_symbol}`}
                      >
                        {fmtPrice(r.target_price)}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-xs ${returnClass(r.target_upside_pct)}`}>
                        {fmtPct(r.target_upside_pct)}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-xs font-semibold ${returnClass(r.return_pct)}`} data-testid={`track-record-return-${r.cse_symbol}`}>
                        {fmtPct(r.return_pct)}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-xs ${returnClass(r.excess_return_pct)}`}>
                        {fmtPct(r.excess_return_pct)}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.correct_price_up == null ? <Minus className="h-3.5 w-3.5 mx-auto text-muted-foreground" />
                          : r.correct_price_up
                            ? <CheckCircle2 className="h-3.5 w-3.5 mx-auto text-[hsl(var(--success))]" />
                            : <XCircle className="h-3.5 w-3.5 mx-auto text-[hsl(var(--destructive))]" />}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.correct_vs_aspi == null ? <Minus className="h-3.5 w-3.5 mx-auto text-muted-foreground" />
                          : r.correct_vs_aspi
                            ? <CheckCircle2 className="h-3.5 w-3.5 mx-auto text-[hsl(var(--success))]" />
                            : <XCircle className="h-3.5 w-3.5 mx-auto text-[hsl(var(--destructive))]" />}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">{r.days_held ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
        <Info className="h-3 w-3" />
        Voting-class shares only (.N0000). Non-voting (.X0000) excluded to avoid mixing different risk/return profiles.
        {summary?.updated_at && (<> · Snapshot as of {new Date(summary.updated_at).toLocaleString("en-GB")}</>)}
      </div>
    </div>
  );
};

const SummaryTile = ({ label, value, sub, icon: Icon, tone, testid }) => {
  const toneClass = tone == null ? "" : tone > 0 ? "text-[hsl(var(--success))]" : tone < 0 ? "text-[hsl(var(--destructive))]" : "";
  return (
    <Card className="border bg-card" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className={`text-2xl font-display font-semibold tabular-nums ${toneClass}`}>{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
          </div>
          {Icon && <Icon className="h-4 w-4 text-primary shrink-0" />}
        </div>
      </CardContent>
    </Card>
  );
};

const HeaderCell = ({ label, sortKey, sortBy, onSort, align = "left" }) => {
  const active = sortBy.key === sortKey;
  const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <TableHead className={`${alignClass} text-[10px] uppercase tracking-wide cursor-pointer select-none whitespace-nowrap`} onClick={() => onSort(sortKey)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sortBy.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </TableHead>
  );
};
